<template>
    <div v-if="hasMarkdown && !isHome" class="page-actions not-prose flex justify-end max-w-7xl mx-auto px-3 mt-4 -mb-4">
        <div ref="root" class="relative inline-block text-left">
            <button type="button" @click="open = !open" :title="copied ? 'Copied!' : 'Copy or share this page'"
                class="inline-flex items-center gap-x-1.5 rounded-md px-3 py-1.5 text-xs font-medium shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-colors bg-white text-[var(--dark-gray)]">
                <Icon v-if="!copied" name="material-symbols:content-copy-outline-rounded" class="text-base" />
                <Icon v-else name="material-symbols:check-small-rounded" class="text-base text-green-500" />
                <span :class="copied ? 'text-green-500' : ''">{{ copied ? 'Copied!' : 'Copy page' }}</span>
                <Icon name="material-symbols:keyboard-arrow-down-rounded" class="text-base" />
            </button>

            <div v-if="open"
                class="absolute right-0 z-30 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 py-1 text-sm text-[var(--dark-gray)]">
                <button type="button" @click="copyMd"
                    class="flex w-full items-center gap-x-2 text-left px-4 py-2 hover:bg-gray-100">
                    <Icon name="material-symbols:content-copy-outline-rounded" />
                    <span>Copy page as Markdown</span>
                </button>
                <a :href="mdPath" target="_blank" rel="noopener" @click="open = false"
                    class="flex items-center gap-x-2 px-4 py-2 hover:bg-gray-100">
                    <Icon name="material-symbols:open-in-new-rounded" />
                    <span>View as Markdown</span>
                </a>
                <a :href="chatGptUrl" target="_blank" rel="noopener" @click="open = false"
                    class="flex items-center gap-x-2 px-4 py-2 hover:bg-gray-100">
                    <Icon name="ri:openai-fill" />
                    <span>Open in ChatGPT</span>
                </a>
                <a :href="claudeUrl" target="_blank" rel="noopener" @click="open = false"
                    class="flex items-center gap-x-2 px-4 py-2 hover:bg-gray-100">
                    <Icon name="ri:robot-2-line" />
                    <span>Open in Claude</span>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { hasMarkdownSibling, mdPathFor, mdUrlFor, normalizePath } from '~~/services/markdownRoutes';

const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const open = ref(false);
const copied = ref(false);
const root = ref<HTMLElement | null>(null);

const normalizedPath = computed(() => normalizePath(route.path));

const hasMarkdown = computed(() => hasMarkdownSibling(normalizedPath.value));

// The "Copy page" element is intentionally omitted from the home page.
const isHome = computed(() => normalizedPath.value === '/');

const mdPath = computed(() => mdPathFor(normalizedPath.value));

const mdUrl = computed(() => {
    const base = String(runtimeConfig.public?.siteUrl || 'https://unified.to');
    return mdUrlFor(normalizedPath.value, base);
});

const prompt = computed(
    () => `Please read ${mdUrl.value} and help me understand this page from Unified.to.`
);
const chatGptUrl = computed(
    () => `https://chatgpt.com/?hints=search&q=${encodeURIComponent(prompt.value)}`
);
const claudeUrl = computed(() => `https://claude.ai/new?q=${encodeURIComponent(prompt.value)}`);

// Advertise the Markdown sibling to crawlers/LLMs on pages that have one.
useHead({
    link: computed(() =>
        hasMarkdown.value
            ? [{ rel: 'alternate', type: 'text/markdown', href: mdUrl.value }]
            : []
    ),
});

async function copyMd() {
    open.value = false;
    try {
        const res = await fetch(mdPath.value, { headers: { Accept: 'text/markdown,text/plain' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
        }
        copied.value = true;
        setTimeout(() => {
            copied.value = false;
        }, 2000);
    } catch (e) {
        console.error('PageActions: failed to copy markdown', e);
    }
}

function onDocClick(e: MouseEvent) {
    if (!open.value || !root.value) return;
    if (!root.value.contains(e.target as Node)) {
        open.value = false;
    }
}

onMounted(() => {
    document.addEventListener('click', onDocClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocClick);
});
</script>
